import { useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "../../../lib/api/client"
import { contactsKeys } from "../../../lib/query-keys"
import { contactOverviewKeys } from "./useContactOverview"
import type { Contact, ContactDivisionSetting } from "../types"
import type { ContactFormData } from "../schemas/contact-form-schema"

/**
 * Hook to create a new contact.
 */
export function useCreateContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ContactFormData) => api.contacts.create(data),
    onSuccess: () => {
      // Invalidate the contacts list to refetch
      queryClient.invalidateQueries({ queryKey: contactsKeys.lists() })
    },
  })
}

/**
 * Hook to update a contact's data.
 */
export function useUpdateContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Contact> }) =>
      api.contacts.update(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate both the overview and the list
      queryClient.invalidateQueries({ queryKey: contactOverviewKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: contactsKeys.lists() })
    },
  })
}

/**
 * Hook to update a contact's division settings.
 */
export function useUpdateContactDivisions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      divisions,
    }: {
      id: string
      divisions: ContactDivisionSetting[]
    }) => api.contacts.divisions(id, { divisions }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: contactOverviewKeys.detail(id) })
    },
  })
}

/**
 * Hook to update a contact's portal password.
 */
export function useUpdateContactPassword() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      api.contacts.password(id, { password }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: contactOverviewKeys.detail(id) })
    },
  })
}
